/**
 * Frontend JavaScript for Posts Filter block
 */

class PostsFilter {
	constructor(container) {
		this.container = container;
		this.postsPerPage = parseInt(container.dataset.postsPerPage) || 6;
		this.showCategoryFilter = container.dataset.showCategoryFilter === 'true';
		this.showSearch = container.dataset.showSearch === 'true';
		this.showPagination = container.dataset.showPagination === 'true';
		this.allowMultipleCategories = container.dataset.allowMultipleCategories !== 'false'; // Default to true
		// Parse selected categories, ensure it's always an array
		try {
			const parsed = JSON.parse(container.dataset.selectedCategories || '[]');
			this.selectedCategories = Array.isArray(parsed) ? parsed : [];
			// Store all selected categories from admin for button display
			this.allSelectedCategories = Array.isArray(parsed) ? [...parsed] : [];
		} catch (e) {
			console.error('Error parsing selectedCategories:', e);
			this.selectedCategories = [];
			this.allSelectedCategories = [];
		}
		this.searchQuery = container.dataset.searchQuery || '';
		this.layout = container.dataset.layout || 'grid';
		this.columns = parseInt(container.dataset.columns) || 3;
		this.showThumbnail = container.dataset.showThumbnail === 'true';
		this.showCategories = container.dataset.showCategories === 'true';
		this.showTags = container.dataset.showTags === 'true';
		this.showDate = container.dataset.showDate === 'true';
		this.showExcerpt = container.dataset.showExcerpt === 'true';
		this.showTitle = container.dataset.showTitle === 'true';
		this.showMetaFields = container.dataset.showMetaFields === 'true';
		// Parse meta fields, ensure it's always an array
		try {
			this.metaFields = JSON.parse(container.dataset.metaFields || '[]');
			if (!Array.isArray(this.metaFields)) {
				this.metaFields = [];
			}
		} catch (e) {
			console.error('Error parsing metaFields:', e);
			this.metaFields = [];
		}
		this.excerptLength = parseInt(container.dataset.excerptLength) || 20;
		this.postType = container.dataset.postType || 'post';
		this.currentPage = 1;
		this.totalPages = 1;
		this.totalPosts = 0;
		this.categories = [];
		this.isLoading = false;

		this.init();
	}

	async init() {
		await this.loadCategories();
		
		// After loading categories, set initial filter to first category
		// This must happen AFTER loadCategories() to ensure categories are loaded
		if (this.allSelectedCategories && this.allSelectedCategories.length > 0 && this.categories && this.categories.length > 0) {
			// Find the first category from allSelectedCategories in the loaded categories
			const firstSelectedId = String(this.allSelectedCategories[0]);
			
			// Check if this category exists in loaded categories
			const categoryExists = this.categories.some(cat => String(cat.id) === firstSelectedId);
			
			if (categoryExists) {
				// Use the first selected category ID as string
				this.selectedCategories = [firstSelectedId];
			} else {
				// If selected category not found, use first available category
				this.selectedCategories = [String(this.categories[0].id)];
			}
		} else if (this.categories && this.categories.length > 0 && this.showCategoryFilter) {
			// If no categories selected in admin but filter is enabled, 
			// automatically select first available category
			this.selectedCategories = [String(this.categories[0].id)];
		}
		
		// Ensure selectedCategories is always an array of strings
		if (!Array.isArray(this.selectedCategories)) {
			this.selectedCategories = [];
		}
		this.selectedCategories = this.selectedCategories.map(id => String(id));
		
		this.setupControls();
		this.loadPosts();
	}

	async loadCategories() {
		try {
			// Load taxonomies for the selected post type
			const response = await fetch(`/wp-json/u-kit/v1/taxonomies?post_type=${encodeURIComponent(this.postType)}`);
			if (response.ok) {
				const taxonomies = await response.json();
				
				// Combine all terms from all taxonomies into a single array
				const combinedTerms = [];
				taxonomies.forEach(taxonomy => {
					taxonomy.terms.forEach(term => {
						combinedTerms.push({
							...term,
							taxonomy: taxonomy.name,
							taxonomyLabel: taxonomy.label,
						});
					});
				});
				
				// Sort by name
				combinedTerms.sort((a, b) => a.name.localeCompare(b.name));
				this.categories = combinedTerms;
			} else {
				// Fallback to default categories if API fails
				const fallbackResponse = await fetch('/wp-json/wp/v2/categories?per_page=100&orderby=name&order=asc');
				if (fallbackResponse.ok) {
					this.categories = await fallbackResponse.json();
				}
			}
		} catch (error) {
			console.error('Error loading categories:', error);
			// Fallback to default categories
			try {
				const fallbackResponse = await fetch('/wp-json/wp/v2/categories?per_page=100&orderby=name&order=asc');
				if (fallbackResponse.ok) {
					this.categories = await fallbackResponse.json();
				}
			} catch (fallbackError) {
				console.error('Error loading fallback categories:', fallbackError);
			}
		}
	}

	setupControls() {
		if (this.showCategoryFilter) {
			this.setupCategoryFilter();
		}

		if (this.showSearch) {
			this.setupSearchFilter();
		}
	}

	setupCategoryFilter() {
		const categoryButtonsContainer = this.container.querySelector('.u-kit-posts-filter-category-buttons');
		if (!categoryButtonsContainer) return;

		// Use allSelectedCategories if available (from admin), otherwise use selectedCategories
		const categoriesToDisplay = this.allSelectedCategories || this.selectedCategories || [];

		// If no categories are selected, hide the filter
		if (!categoriesToDisplay || categoriesToDisplay.length === 0) {
			const categoriesContainer = this.container.querySelector('.u-kit-posts-filter-categories');
			if (categoriesContainer) {
				categoriesContainer.style.display = 'none';
			}
			return;
		}

		// Show the filter container
		const categoriesContainer = this.container.querySelector('.u-kit-posts-filter-categories');
		if (categoriesContainer) {
			categoriesContainer.style.display = '';
		}

		// Filter categories to show only selected ones from admin
		// Ensure categoriesToDisplay is an array
		if (!Array.isArray(categoriesToDisplay)) {
			return;
		}
		
		// Create a map of categories by ID for quick lookup
		const categoriesMap = new Map();
		this.categories.forEach(category => {
			categoriesMap.set(category.id.toString(), category);
		});
		
		// Get categories in the order they were selected in admin
		const categoriesToShow = categoriesToDisplay
			.map(id => {
				const categoryIdStr = id.toString();
				return categoriesMap.get(categoryIdStr);
			})
			.filter(category => category !== undefined); // Remove any categories that weren't found

		// Add category buttons only for selected categories in admin order
		categoriesToShow.forEach(category => {
			const button = document.createElement('button');
			button.className = 'u-kit-posts-filter-category-btn';
			button.textContent = category.name;
			button.dataset.categoryId = category.id;
			button.addEventListener('click', () => this.filterByCategory(category.id));
			categoryButtonsContainer.appendChild(button);
		});

		// Ensure selectedCategories is set to first category
		// Priority: use selectedCategories from init() if set, otherwise use first from allSelectedCategories
		if (this.selectedCategories && this.selectedCategories.length > 0) {
			// selectedCategories already set in init(), just ensure it's array of strings
			this.selectedCategories = this.selectedCategories.map(id => String(id));
		} else if (this.allSelectedCategories && this.allSelectedCategories.length > 0 && categoriesToShow.length > 0) {
			// Use first category from allSelectedCategories
			const firstSelectedId = String(this.allSelectedCategories[0]);
			// Verify this category exists in categoriesToShow
			const categoryExists = categoriesToShow.some(cat => String(cat.id) === firstSelectedId);
			if (categoryExists) {
				this.selectedCategories = [firstSelectedId];
			} else if (categoriesToShow.length > 0) {
				// Fallback to first available category
				this.selectedCategories = [String(categoriesToShow[0].id)];
			}
		} else if (categoriesToShow.length > 0) {
			// No categories selected in admin, use first available
			this.selectedCategories = [String(categoriesToShow[0].id)];
		}
		
		// Final check: ensure selectedCategories is array of strings
		if (!Array.isArray(this.selectedCategories) || this.selectedCategories.length === 0) {
			if (categoriesToShow.length > 0) {
				this.selectedCategories = [String(categoriesToShow[0].id)];
			} else {
				this.selectedCategories = [];
			}
		} else {
			this.selectedCategories = this.selectedCategories.map(id => String(id));
		}

		// Set initial active state
		this.updateCategoryButtons();
	}

	setupSearchFilter() {
		const searchInput = this.container.querySelector('.u-kit-posts-filter-search-input');
		if (!searchInput) return;

		searchInput.style.display = 'block';
		searchInput.value = this.searchQuery;

		// Debounced search
		let searchTimeout;
		searchInput.addEventListener('input', (e) => {
			clearTimeout(searchTimeout);
			searchTimeout = setTimeout(() => {
				this.searchQuery = e.target.value;
				this.currentPage = 1;
				this.loadPosts();
			}, 500);
		});
	}

	filterByCategory(categoryId) {
		const categoryIdStr = categoryId.toString();
		
		// Ensure selectedCategories is an array
		if (!Array.isArray(this.selectedCategories)) {
			this.selectedCategories = [];
		}
		
		if (this.allowMultipleCategories) {
			// Multiple selection mode: toggle category
			const index = this.selectedCategories.indexOf(categoryIdStr);
			
			if (index > -1) {
				// Remove category if already selected
				this.selectedCategories.splice(index, 1);
			} else {
				// Add category if not selected
				this.selectedCategories.push(categoryIdStr);
			}
		} else {
			// Single selection mode: replace current selection
			const index = this.selectedCategories.indexOf(categoryIdStr);
			
			if (index > -1 && this.selectedCategories.length === 1) {
				// If clicking the same category and it's the only one, deselect it
				this.selectedCategories = [];
			} else {
				// Select only this category
				this.selectedCategories = [categoryIdStr];
			}
		}
		
		// Create a new array to ensure reactivity
		this.selectedCategories = [...this.selectedCategories];
		
		this.currentPage = 1;
		this.updateCategoryButtons();
		this.loadPosts();
	}

	updateCategoryButtons() {
		const buttons = this.container.querySelectorAll('.u-kit-posts-filter-category-btn');
		
		// Ensure selectedCategories is an array of strings
		if (!Array.isArray(this.selectedCategories)) {
			this.selectedCategories = [];
		}
		const selectedIds = this.selectedCategories.map(id => String(id));
		
		buttons.forEach(button => {
			const categoryId = button.dataset.categoryId;
			// Ensure we're comparing strings
			const categoryIdStr = categoryId ? String(categoryId) : '';
			const isSelected = selectedIds.includes(categoryIdStr);
			button.classList.toggle('active', isSelected);
		});
	}

	async loadPosts() {
		if (this.isLoading) return;

		this.isLoading = true;
		this.showLoading();

		try {
			// Check if uKitPostsFilter is available, if not, try to get it from window
			if (typeof uKitPostsFilter === 'undefined') {
				// Try to get from window object
				if (typeof window.uKitPostsFilter !== 'undefined') {
					window.uKitPostsFilter = window.uKitPostsFilter;
				} else {
					// Fallback: create a basic configuration
					window.uKitPostsFilter = {
						ajaxUrl: '/wp-admin/admin-ajax.php',
						nonce: ''
					};
					console.warn('uKitPostsFilter not found, using fallback configuration');
				}
			}

			const formData = new FormData();
			formData.append('action', 'u_kit_posts_filter');
			formData.append('nonce', uKitPostsFilter.nonce);
			formData.append('posts_per_page', this.postsPerPage);
			formData.append('page', this.currentPage);
			formData.append('search_query', this.searchQuery);
			formData.append('layout', this.layout);
			formData.append('columns', this.columns);
			formData.append('show_thumbnail', this.showThumbnail ? 'true' : 'false');
			formData.append('show_categories', this.showCategories ? 'true' : 'false');
			formData.append('show_tags', this.showTags ? 'true' : 'false');
			formData.append('show_date', this.showDate ? 'true' : 'false');
			formData.append('show_excerpt', this.showExcerpt ? 'true' : 'false');
			formData.append('show_title', this.showTitle ? 'true' : 'false');
			formData.append('show_meta_fields', this.showMetaFields ? 'true' : 'false');
			formData.append('excerpt_length', this.excerptLength);
			formData.append('post_type', this.postType);
			
			// Always send categories array, even if empty
			// Convert string IDs to integers for PHP
			if (!Array.isArray(this.selectedCategories)) {
				this.selectedCategories = [];
			}
			const categoryIds = this.selectedCategories
				.map(id => {
					const numId = parseInt(id.toString(), 10);
					return isNaN(numId) ? null : numId;
				})
				.filter(id => id !== null && id > 0);
			
			const categoriesJson = JSON.stringify(categoryIds);
			formData.append('categories', categoriesJson);

			const response = await fetch(uKitPostsFilter.ajaxUrl, {
				method: 'POST',
				body: formData,
			});
			
			if (response.ok) {
				const data = await response.json();
				if (data.success) {
					this.totalPages = data.total_pages || 1;
					this.totalPosts = data.total_posts || 0;
					this.renderPosts(data.posts);
					this.renderPagination(data.posts);
				} else {
					this.renderError('Failed to load posts');
				}
			} else {
				this.renderError('Failed to load posts');
			}
		} catch (error) {
			console.error('Error loading posts:', error);
			this.renderError('Error loading posts');
		} finally {
			this.isLoading = false;
			this.hideLoading();
		}
	}

	/**
	 * Escape HTML to prevent XSS attacks
	 * @param {string} text - Text to escape
	 * @returns {string} Escaped text
	 */
	escapeHtml(text) {
		if (typeof text !== 'string') {
			return '';
		}
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}

	renderPosts(posts) {
		const postsContainer = this.container.querySelector('.u-kit-posts-filter-posts');
		if (!postsContainer) return;

		postsContainer.innerHTML = '';
		
		// Update container classes for layout
		postsContainer.className = 'u-kit-posts-filter-posts';
		if (this.layout === 'grid') {
			postsContainer.classList.add('u-kit-posts-filter-grid', `u-kit-posts-filter-columns-${this.columns}`);
		} else {
			postsContainer.classList.add('u-kit-posts-filter-list');
		}

		if (posts.length === 0) {
			// Use WordPress i18n if available, otherwise use original text
			const noPostsText = (typeof wp !== 'undefined' && wp.i18n && wp.i18n.__) 
				? wp.i18n.__('No posts found', 'u-kit') 
				: 'No posts found';
			postsContainer.innerHTML = `<div class="u-kit-posts-filter-no-posts">${this.escapeHtml(noPostsText)}</div>`;
			return;
		}

		posts.forEach(post => {
			const postElement = document.createElement('div');
			postElement.className = `u-kit-posts-filter-post u-kit-posts-filter-post-${this.layout}`;
			
			let postHTML = '';
			
			// Thumbnail
			if (this.showThumbnail && post.thumbnail) {
				postHTML += `
					<div class="u-kit-posts-filter-post-thumbnail">
						<a href="${this.escapeHtml(post.link)}">
							<img src="${this.escapeHtml(post.thumbnail)}" alt="${this.escapeHtml(post.title)}" />
						</a>
					</div>
				`;
			}
			
			postHTML += '<div class="u-kit-posts-filter-post-content">';
			
			// Title
			if (this.showTitle) {
				postHTML += `
					<div class="u-kit-posts-filter-post-title">
						<a href="${this.escapeHtml(post.link)}">${this.escapeHtml(post.title)}</a>
					</div>
				`;
			}
			
			// Excerpt - already escaped on server, but double-check
			if (this.showExcerpt && post.excerpt) {
				postHTML += `
					<div class="u-kit-posts-filter-post-excerpt">
						${post.excerpt}
					</div>
				`;
			}
			
			// Categories
			if (this.showCategories && post.categories && post.categories.length > 0) {
				postHTML += `
					<div class="u-kit-posts-filter-post-categories">
						${post.categories.map(cat => 
							`<a href="${this.escapeHtml(cat.link)}" class="u-kit-posts-filter-post-category">${this.escapeHtml(cat.name)}</a>`
						).join('')}
					</div>
				`;
			}
			
			// Tags
			if (this.showTags && post.tags && post.tags.length > 0) {
				postHTML += `
					<div class="u-kit-posts-filter-post-tags">
						${post.tags.map(tag => 
							`<a href="${this.escapeHtml(tag.link)}" class="u-kit-posts-filter-post-tag">${this.escapeHtml(tag.name)}</a>`
						).join('')}
					</div>
				`;
			}
			
			// Date
			if (this.showDate && post.date) {
				postHTML += `
					<div class="u-kit-posts-filter-post-date">
						${post.date}
					</div>
				`;
			}
			
			// Meta fields
			if (this.showMetaFields && post.meta && Object.keys(post.meta).length > 0) {
				postHTML += `
					<div class="u-kit-posts-filter-post-meta">
						${Object.entries(post.meta).map(([key, value]) => {
							// Format key for display
							let formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
							
							// Special formatting for space fields
							if (key.startsWith('_space_')) {
								formattedKey = formattedKey.replace('Space ', '').replace('Space', '');
							}
							
							// Handle different types of meta values
							let displayValue = value;
							
							// Special handling for specific meta fields first
							if (key === '_space_working_hours' && Array.isArray(value)) {
								// Format working hours array
								displayValue = `
									<div class="u-kit-meta-nested">
										${value.length > 0 ? value.map((hour, index) => {
											if (typeof hour === 'object' && hour !== null) {
												// Check if we have both day and time
												if (hour.day && hour.time) {
													return `<div><strong>${hour.day}:</strong> ${hour.time}</div>`;
												} else if (hour.day && !hour.time) {
													// Only day is filled
													return `<div><strong>${hour.day}</strong></div>`;
												} else if (!hour.day && hour.time) {
													// Only time is filled
													return `<div><strong>Time:</strong> ${hour.time}</div>`;
												} else {
													// Handle other object structures
													return `<div>${JSON.stringify(hour)}</div>`;
												}
											} else {
												return `<div>${hour}</div>`;
											}
										}).join('') : '<div>No working hours specified</div>'}
									</div>
								`;
							} else if (Array.isArray(value)) {
								displayValue = value.join(', ');
							} else if (typeof value === 'object' && value !== null) {
								// Format JSON objects more nicely
								if (key === 'event_details') {
									const details = value;
									displayValue = `
										<div class="u-kit-meta-nested">
											${details.start_date ? `<div><strong>Start:</strong> ${details.start_date} ${details.start_time || ''}</div>` : ''}
											${details.end_date ? `<div><strong>End:</strong> ${details.end_date} ${details.end_time || ''}</div>` : ''}
											${details.all_day !== undefined ? `<div><strong>All Day:</strong> ${details.all_day ? 'Yes' : 'No'}</div>` : ''}
										</div>
									`;
								} else if (key === 'event_registration_details') {
									const reg = value;
									displayValue = `
										<div class="u-kit-meta-nested">
											${reg.label ? `<div><strong>Label:</strong> ${reg.label}</div>` : ''}
											${reg.url ? `<div><strong>URL:</strong> <a href="${reg.url}" target="_blank">${reg.url}</a></div>` : ''}
											${reg.show_link !== undefined ? `<div><strong>Show Link:</strong> ${reg.show_link ? 'Yes' : 'No'}</div>` : ''}
											${reg.expires_at ? `<div><strong>Expires:</strong> ${reg.expires_at}</div>` : ''}
										</div>
									`;
								} else {
									displayValue = JSON.stringify(value, null, 2);
								}
							} else if (key.includes('timestamp')) {
								// Format timestamps as readable dates
								const date = new Date(value * 1000);
								displayValue = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
							}
							
							// Create CSS class from key (remove underscores and convert to lowercase)
							const fieldClass = key.replace(/_/g, '-').toLowerCase();
							
							return `<div class="u-kit-posts-filter-post-meta-field u-kit-posts-filter-post-meta-field-${fieldClass}">
								<span class="u-kit-posts-filter-post-meta-key">${formattedKey}:</span>
								<span class="u-kit-posts-filter-post-meta-value">${displayValue}</span>
							</div>`;
						}).join('')}
					</div>
				`;
			}
			
			postHTML += '</div>';
			postElement.innerHTML = postHTML;
			
			postsContainer.appendChild(postElement);
		});
	}

	renderPagination(posts = []) {
		const paginationContainer = this.container.querySelector('.u-kit-posts-filter-pagination');
		if (!paginationContainer) return;

		// Clear pagination container first
		paginationContainer.innerHTML = '';

		// Hide pagination if:
		// 1. Pagination is disabled
		// 2. Total pages is 1 or less
		// 3. Total posts is less than or equal to posts per page
		if (!this.showPagination || 
			this.totalPages <= 1 || 
			(this.totalPosts > 0 && this.totalPosts <= this.postsPerPage)) {
			return;
		}

		// Previous button
		if (this.currentPage > 1) {
			const prevButton = document.createElement('button');
			prevButton.className = 'u-kit-posts-filter-pagination-btn';
			// Use WordPress i18n if available, otherwise use original text
			const prevText = (typeof wp !== 'undefined' && wp.i18n && wp.i18n.__) 
				? wp.i18n.__('Previous', 'u-kit') 
				: 'Previous';
			prevButton.textContent = prevText;
			prevButton.addEventListener('click', () => {
				this.currentPage--;
				this.loadPosts();
			});
			paginationContainer.appendChild(prevButton);
		}

		// Page numbers
		const startPage = Math.max(1, this.currentPage - 2);
		const endPage = Math.min(this.totalPages, this.currentPage + 2);

		for (let i = startPage; i <= endPage; i++) {
			const pageButton = document.createElement('button');
			pageButton.className = `u-kit-posts-filter-pagination-btn ${i === this.currentPage ? 'active' : ''}`;
			pageButton.textContent = i;
			pageButton.addEventListener('click', () => {
				this.currentPage = i;
				this.loadPosts();
			});
			paginationContainer.appendChild(pageButton);
		}

		// Next button
		if (this.currentPage < this.totalPages) {
			const nextButton = document.createElement('button');
			nextButton.className = 'u-kit-posts-filter-pagination-btn';
			// Use WordPress i18n if available, otherwise use original text
			const nextText = (typeof wp !== 'undefined' && wp.i18n && wp.i18n.__) 
				? wp.i18n.__('Next', 'u-kit') 
				: 'Next';
			nextButton.textContent = nextText;
			nextButton.addEventListener('click', () => {
				this.currentPage++;
				this.loadPosts();
			});
			paginationContainer.appendChild(nextButton);
		}
	}

	showLoading() {
		const postsContainer = this.container.querySelector('.u-kit-posts-filter-posts');
		const loadingElement = this.container.querySelector('.u-kit-posts-filter-loading');
		
		if (postsContainer) {
			// Make posts transparent
			postsContainer.classList.add('u-kit-posts-filter-loading-state');
		}
		
		if (loadingElement) {
			loadingElement.style.display = 'flex';
			// Create spinner if it doesn't exist
			if (!loadingElement.querySelector('.u-kit-posts-filter-spinner')) {
				loadingElement.innerHTML = '<div class="u-kit-posts-filter-spinner"></div>';
			}
		}
	}

	hideLoading() {
		const postsContainer = this.container.querySelector('.u-kit-posts-filter-posts');
		const loadingElement = this.container.querySelector('.u-kit-posts-filter-loading');
		
		if (postsContainer) {
			// Remove transparency
			postsContainer.classList.remove('u-kit-posts-filter-loading-state');
		}
		
		if (loadingElement) {
			loadingElement.style.display = 'none';
		}
	}

	renderError(message) {
		const postsContainer = this.container.querySelector('.u-kit-posts-filter-posts');
		if (postsContainer) {
			postsContainer.innerHTML = `<div class="u-kit-posts-filter-error">${message}</div>`;
		}
	}
}

// Initialize posts filters when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
	const postsFilterContainers = document.querySelectorAll('.u-kit-posts-filter');
	postsFilterContainers.forEach(container => {
		new PostsFilter(container);
	});
});
